import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { Dialog, DialogActions, DialogContent, FormHelperText, } from "@mui/material";
import Link from "@mui/material/Link";
import makeStyles from "@mui/styles/makeStyles";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import LoadingButton from "../../../components/LoadingButton";
import { callAPI } from "../../../lib/common";
import Button from "@mui/material/Button";
import BoxActions from "./BoxActions";
import { useAuth } from "../../providers/AuthProvider";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import FormikTextField from "../../../components/Common/FormikTextField";
import Level2BoxContainer from "./Level2BoxContainer";
import { Level2BoxHeading } from "./Level2BoxHeading";
const useStyles = makeStyles(() => ({
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
    },
    form: {
        flexGrow: 1,
        marginBottom: 0,
    },
}));
const Login = ({ email, handleGoBack, handleLoginSuccess }) => {
    const { t } = useTranslation();
    const { isAuthenticating, loginError, login: authLogin } = useAuth();
    const classes = useStyles();
    const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState(false);
    const isMobile = useIsMobileDevice();
    const openSendForgotPasswordDialog = (e) => {
        e.preventDefault();
        setForgotPasswordDialogOpen(true);
    };
    return (React.createElement(Level2BoxContainer, null,
        React.createElement(Level2BoxHeading, null, t("Welcome back, %s", { postProcess: "sprintf", sprintf: [email] })),
        React.createElement(Box, { mb: 1, sx: { textAlign: "center" } },
            React.createElement(Trans, null, "Please enter your password")),
        React.createElement(Formik, { initialValues: { password: "" }, validationSchema: loginSchema(t), onSubmit: (values) => {
                authLogin(email, values.password)
                    .then((result) => {
                    if (result.user &&
                        window.isEpmsAnalyticsFeatureEnabled &&
                        window.epmsAnalytics) {
                        window.epmsAnalytics.onLogin(result.user);
                    }
                    handleLoginSuccess(result);
                })
                    .catch(() => { });
            } }, ({ submitForm, isValid }) => {
            return (React.createElement(Form, { className: classes.form },
                React.createElement(Field, { id: "new-subject-code-password", component: FormikTextField, type: "password", label: t("Password"), name: "password", variant: "outlined", fullWidth: true, autoFocus: true }),
                React.createElement(Box, { mt: 1, mb: 4, display: "flex", justifyContent: "flex-end" },
                    React.createElement(Link, { href: "#", onClick: openSendForgotPasswordDialog },
                        React.createElement(Trans, null, "Forgot your password?")),
                    " "),
                React.createElement(FormHelperText, { error: !!loginError }, loginError),
                React.createElement(BoxActions, null,
                    React.createElement(Button, { variant: "outlined", color: "primary", onClick: handleGoBack, size: isMobile ? "medium" : "large" },
                        React.createElement(Trans, null, "Go back")),
                    React.createElement(LoadingButton, { variant: "primary", isLoading: isAuthenticating, onClick: submitForm, disabled: !isValid, fullWidth: true, size: isMobile ? "medium" : "large" },
                        React.createElement(Trans, null, "Log in"))),
                forgotPasswordDialogOpen && (React.createElement(ForgotPasswordDialog, { email: email, onClose: () => setForgotPasswordDialogOpen(false) }))));
        })));
};
const ForgotPasswordDialog = ({ email, onClose, }) => {
    const [sending, setSending] = useState(true);
    const [error, setError] = useState(null);
    const [completeMessage, setCompleteMessage] = useState("");
    const { t } = useTranslation();
    useEffect(() => {
        callAPI(`/sections/prophoto/ajax/send_password_reminder.php?json=1&email=${email}`)
            .then((res) => {
            if (res.error) {
                setError(res.error);
            }
            else {
                setCompleteMessage(res.message);
            }
        })
            .catch(() => {
            setError(t("Error sending password reset instructions."));
        })
            .finally(() => {
            setSending(false);
        });
    }, [email, t]);
    const getContent = () => {
        if (sending) {
            return (React.createElement(DialogContent, null,
                React.createElement(Trans, null, "Sending...")));
        }
        if (error || completeMessage) {
            return (React.createElement(React.Fragment, null,
                React.createElement(DialogContent, null, error || completeMessage),
                React.createElement(DialogActions, null,
                    React.createElement(Button, { color: "primary", onClick: onClose },
                        React.createElement(Trans, null, "Ok")))));
        }
    };
    return React.createElement(Dialog, { open: true }, getContent());
};
const loginSchema = (t) => () => {
    return Yup.object().shape({
        password: Yup.string()
            .min(3, t("That's too short!"))
            .required(t("This is required!")),
    });
};
export default Login;
